import React from 'react';

import { hasChunkReloadFlag, isChunkLoadError, tryChunkReloadOnce } from '../lib/chunkReloadGuard';
import { captureError } from '../lib/sentry';
import { imgPlaceholder } from '../lib/placeholderImage';
import { loadLanguage } from '../lib/onboardingStorage';
import { getCachedDictionaryFallbackChain } from '../locales';
import { translateWithFallback } from '../lib/translate';

const FALLBACK_IMG = imgPlaceholder(120, 120, 'err');

function isDebugQuery() {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).has('debug');
}

function firstLines(str, n) {
  return String(str || '')
    .split('\n')
    .slice(0, n)
    .join('\n');
}

const BOUNDARY_COPY = {
  title: '일시적인 오류가 발생했습니다',
  offline: '인터넷 연결을 확인해주세요.',
  generic: '앱을 새로고침하거나 잠시 후 다시 시도해 주세요.',
  reload: '새로고침',
};

function boundaryT(key) {
  try {
    const chain = getCachedDictionaryFallbackChain(loadLanguage() || 'ko');
    if (!chain.length) return BOUNDARY_COPY[key.split('.').pop()] || key;
    const translated = translateWithFallback(chain, key);
    return translated === key ? BOUNDARY_COPY[key.split('.').pop()] || key : translated;
  } catch {
    return BOUNDARY_COPY[key.split('.').pop()] || key;
  }
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      isOffline: false,
      isChunkRecovering: false,
      message: '',
      debugError: null,
      debugInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    const msg = String(error?.message || '');
    if (isChunkLoadError(error) && !hasChunkReloadFlag()) {
      return { hasError: true, isChunkRecovering: true, isOffline: false, message: msg };
    }
    const offline =
      typeof navigator !== 'undefined' &&
      !navigator.onLine &&
      (msg.includes('fetch') || msg.includes('network') || msg.includes('Failed'));
    return {
      hasError: true,
      isChunkRecovering: false,
      isOffline: offline,
      message: msg,
    };
  }

  componentDidCatch(error, info) {
    if (tryChunkReloadOnce(error)) return;
    // Second (or non-chunk) failure: show the reload UI instead of a blank screen.
    captureError(error, { componentStack: info?.componentStack });
    this.setState({ debugError: error, debugInfo: info, isChunkRecovering: false });
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary]', error, info);
    }
  }

  componentDidUpdate(prevProps) {
    // Route change after a crash → clear so other screens are usable without full reload.
    if (this.props.resetKey !== prevProps.resetKey && this.state.hasError) {
      this.setState({
        hasError: false,
        isOffline: false,
        isChunkRecovering: false,
        message: '',
        debugError: null,
        debugInfo: null,
      });
    }
  }

  render() {
    // Brief blank only while the one-shot location.reload() is in flight.
    if (this.state.hasError && this.state.isChunkRecovering && !hasChunkReloadFlag()) {
      return null;
    }
    if (this.state.hasError) {
      const offline = this.state.isOffline || (typeof navigator !== 'undefined' && !navigator.onLine);
      return (
        <div className="mingo-error-boundary">
          <img src={FALLBACK_IMG} alt="" width={72} height={72} className="mingo-error-boundary__img" />
          <h1 className="mingo-error-boundary__title">{boundaryT('errorBoundary.title')}</h1>
          <p className="mingo-error-boundary__body">
            {offline ? boundaryT('errorBoundary.offline') : boundaryT('errorBoundary.generic')}
          </p>
          <button type="button" className="mingo-error-boundary__btn" onClick={() => window.location.reload()}>
            {boundaryT('errorBoundary.reload')}
          </button>
          {isDebugQuery() && this.state.debugError ? (
            <pre
              className="mingo-error-boundary__debug"
              style={{
                marginTop: 16,
                padding: 12,
                maxWidth: '100%',
                overflow: 'auto',
                fontSize: 11,
                lineHeight: 1.4,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                textAlign: 'left',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                userSelect: 'text',
                WebkitUserSelect: 'text',
                background: 'rgba(0,0,0,0.06)',
                borderRadius: 8,
              }}
            >
              {`${this.state.debugError.name || 'Error'}: ${this.state.debugError.message || this.state.message || ''}`}
              {'\n\n— stack —\n'}
              {firstLines(this.state.debugError.stack, 5)}
              {'\n\n— componentStack —\n'}
              {firstLines(this.state.debugInfo?.componentStack, 5)}
            </pre>
          ) : null}
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
